import { Button, Col, Input, Modal, Row } from "antd";
import { CopyIcon, EmptyStarIcon, FullStarIcon } from "../../assets";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../redux/hooks";
import {
  initExamRunning,
  setExamLab,
  setExamLeftTime,
  stopExamRunning,
} from "../../redux/apiSlice/examSlice";
import axiosClient from "../../api/axiosClient";
import { IconPrimary, Loading } from "../../components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import modifyId from "../../utils/modifyId";
import handleApiError from "../../utils/handleError";
import downloadFile from "../../utils/downloadFile";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { finishExam } from "../../utils/finishExam";

const questionStatusLegend = [
  { statusClass: "question-answered", label: "Answered" },
  { statusClass: "question-mark", label: "Marked" },
  { statusClass: "question-not-answered", label: "Not answered" },
  { statusClass: "question-not-visited", label: "Not visited" },
];

export default function ActiveSessionExam() {
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
  const [questions, setQuestions] = useState<any>([]);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const params = useParams();
  const [flagValue, setFlagValue] = useState("");
  const navigate = useNavigate();
  const [saveInstance, setSavedInstance] = useState<any>({});
  // Answers already saved on the server get folded into `questions` once both the
  // paper and the saved submission have arrived. The ref keeps that a one-shot, so a
  // later save (which rewrites saveInstance) can't clobber in-page state.
  const hydratedRef = useRef(false);
  const flagPrefillRef = useRef<string | null>(null);

  // Support both in-app navigation (router state) and a cold SSO deep-link
  // (/my-session/active/:id from the Experience portal), where no router state exists.
  const instanceId = location.state?.exam_instance?.id ?? params.id;

  // Re-entering a running exam (reload, closed window, SSO deep-link) refetches the
  // paper, and the paper comes back with no answers on it — those live on the saved
  // submission. Fold them in, so the chosen option is selected again, the palette
  // shows Answered, and the next save doesn't overwrite the stored answers with the
  // blank ones this page started with.
  useEffect(() => {
    if (hydratedRef.current) return;
    const submissions = saveInstance?.exam_submission?.question_submissions;
    if (!questions.length || !submissions) return;

    hydratedRef.current = true;

    setQuestions((prevQuestions: any) =>
      prevQuestions.map((question: any) => {
        const saved = submissions.find(
          (submission: any) => submission.question_id === question.id
        );
        const savedAnswer = saved?.answer || "";
        if (!savedAnswer) return question;
        return {
          ...question,
          selectedAnswer: savedAnswer,
          answerStatus: "question-answered",
        };
      })
    );
  }, [saveInstance, questions]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    async function getQuestions(attempt = 0) {
      try {
        setLoading(true);
        const res = await axiosClient.get(
          `examinstances/${instanceId}/questions`
        );
        if (cancelled) return;
        setQuestions(res.data.questions);
        // Drive the countdown from the questions response so a cold deep-link
        // (no router state) still gets the correct end time. end_time is
        // started_at + duration, stamped when the examiner verified — not the booked
        // slot's end — so the full duration is counted from when questions unlocked.
        //
        // useTimer measures against the browser clock, so shift the target by the
        // browser/server offset: a machine running fast or slow (or deliberately set
        // back) then still gets exactly its duration, no more and no less.
        const serverNow = Number(res.data.server_time) || 0;
        const skew = serverNow ? Math.round(Date.now() / 1000) - serverNow : 0;
        dispatch(setExamLeftTime({ time: Number(res.data.end_time) + skew }));
        setLoading(false);
      } catch (error: any) {
        if (cancelled) return;
        // A cold SSO deep-link can reach this page a beat before the new portal
        // session settles. A session-shaped 401/403 then means "not authorised
        // yet", not "not verified" — retry briefly rather than bouncing a
        // already-verified student out of the exam they just opened.
        const httpStatus = error?.response?.status;
        const apiStatus = String(error?.response?.data?.status ?? "").toLowerCase();
        const sessionError =
          (httpStatus === 401 || httpStatus === 403) &&
          (apiStatus.includes("auth") ||
            apiStatus.includes("session") ||
            apiStatus.includes("login again"));
        if (sessionError && attempt < 2) {
          retryTimer = setTimeout(() => getQuestions(attempt + 1), 1200);
          return;
        }
        // Carry the instance through: NotVerified is also reachable without router
        // state on the deep-link path, and it needs the id to poll the gate.
        navigate("/my-session/not-verified", {
          state: {
            ...(location.state || {}),
            exam_instance: location.state?.exam_instance ?? { id: instanceId },
          },
          replace: true,
        });
      }
    }
    getQuestions();

    async function getSavedInstance() {
      try {
        const res = await axiosClient.post(
          `examinstances/${instanceId}/start`
        );

        setSavedInstance(res.data);
        // The header lives in Dashboard, so the lab flags travel through the
        // store: they decide whether Reset Lab / Download VPN Config are offered.
        dispatch(
          setExamLab({
            available: res.data.lab_available,
            status: res.data.lab_status,
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    }
    getSavedInstance();

    dispatch(initExamRunning());
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      dispatch(stopExamRunning());
    };
    // eslint-disable-next-line
  }, [dispatch, instanceId]);

  // Picking an option is the submit for MCQs — the answer is saved straight away
  // so the student only has to use Next/Prev to move through the paper.
  function handleChooseOption(optionNumber: number) {
    const updatedQuestions = [...questions];
    const current = updatedQuestions[currentQuestionNumber];

    updatedQuestions[currentQuestionNumber] = {
      ...current,
      selectedAnswer: current.option[optionNumber],
      answerStatus: current.marked ? "question-mark" : "question-answered",
    };

    setQuestions(updatedQuestions);
    persistAnswers(updatedQuestions);
  }

  function handleMarked() {
    setQuestions((prevQuestions: any) => {
      const updatedQuestions = [...prevQuestions];
      // Replace the current question with the updated question
      updatedQuestions[currentQuestionNumber] = {
        ...updatedQuestions[currentQuestionNumber],
        marked: !prevQuestions[currentQuestionNumber].marked,
        answerStatus: !prevQuestions[currentQuestionNumber].marked
          ? "question-mark"
          : prevQuestions[currentQuestionNumber].selectedAnswer
          ? "question-answered"
          : "question-not-answered",
      };

      return updatedQuestions;
    });
  }

  function goToNextQuestion() {
    setCurrentQuestionNumber(currentQuestionNumber + 1);
    handleNotAnswered();
  }

  function goToPrevQuestion() {
    setCurrentQuestionNumber(currentQuestionNumber - 1);
    handleNotAnswered();
  }

  function handleNotAnswered() {
    setQuestions((prevQuestions: any) => {
      const updatedQuestions = [...prevQuestions];
      // Replace the current question with the updated question
      updatedQuestions[currentQuestionNumber] = {
        ...updatedQuestions[currentQuestionNumber],
        answerStatus: prevQuestions[currentQuestionNumber].marked
          ? "question-mark"
          : prevQuestions[currentQuestionNumber].selectedAnswer
          ? "question-answered"
          : "question-not-answered",
      };
      return updatedQuestions;
    });
  }

  // Flag answers live in a text box, so moving between questions has to carry the box
  // with it: prefill from the saved answer, and never let one question's typing leak
  // into the next. Keyed on question id + saved answer, so typing (which touches
  // neither) is never wiped by a re-render, while the answer arriving from the saved
  // submission does refill the box.
  useEffect(() => {
    const question = questions[currentQuestionNumber];
    if (!question) return;
    const savedAnswer = question.selectedAnswer || "";
    const prefillKey = `${question.id}|${savedAnswer}`;
    if (flagPrefillRef.current === prefillKey) return;
    flagPrefillRef.current = prefillKey;
    setFlagValue(question.type === "flag" ? savedAnswer : "");
  }, [questions, currentQuestionNumber]);

  // Takes the questions list to persist explicitly, so a caller that has just
  // built an updated list (option click) doesn't save the pre-click state.
  async function persistAnswers(questionsToSave: any[]) {
    const question_submissions = questionsToSave.map(
      ({ id, selectedAnswer }: any) => {
        return {
          question_id: id,
          answer: selectedAnswer || "",
        };
      }
    );

    const updatedSaveInstance = {
      ...saveInstance,
      exam_submission: {
        ...saveInstance.exam_submission,
        question_submissions: question_submissions,
      },
    };

    setSavedInstance(updatedSaveInstance);
    try {
      await axiosClient.patch(
        "examinstances/savesubmission",
        updatedSaveInstance.exam_submission
      );
    } catch (error) {
      handleApiError(error);
    }
  }

  // Flag questions still need an explicit submit — the student types the answer.
  async function saveExamInstance() {
    const trimmedFlag = flagValue.trim();
    const updatedQuestions = [...questions];
    updatedQuestions[currentQuestionNumber] = {
      ...updatedQuestions[currentQuestionNumber],
      selectedAnswer: trimmedFlag,
      answerStatus: updatedQuestions[currentQuestionNumber].marked
        ? "question-mark"
        : trimmedFlag
        ? "question-answered"
        : "question-not-answered",
    };

    setQuestions(updatedQuestions);
    // Keep the box showing what was saved rather than blanking it — this is the
    // answer the student sees on the question from now on.
    setFlagValue(trimmedFlag);

    await persistAnswers(updatedQuestions);
  }

  async function submitTest() {
    try {
      await axiosClient.post(`examinstances/${instanceId}/submitexam`);
      finishExam("Congratulation! Your exam is submitted.", () =>
        navigate("/my-session", { replace: true })
      );
    } catch (error) {
      handleApiError(error);
    }
  }

  if (loading) return <Loading />;

  // An exam with no questions configured returns questions: null/[] from the API.
  // Guard here so the page shows an empty state instead of crashing on questions[0].
  if (!questions || questions.length === 0) {
    return (
      <div className="my-active-session-container">
        <div
          className="active-session-question-container"
          style={{ textAlign: "center", padding: 40 }}
        >
          <h4>No questions available for this exam yet.</h4>
          <p style={{ marginTop: 10 }}>
            This exam has no questions configured. Please contact the support
            team or your examiner.
          </p>
          <Button
            type="primary"
            style={{ marginTop: 20 }}
            onClick={() => navigate("/my-session")}
          >
            Back to My Sessions
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionNumber];

  const answeredCount = questions.filter(
    (question: any) =>
      question.selectedAnswer ||
      saveInstance?.exam_submission?.question_submissions?.some(
        (submission: any) =>
          submission.question_id === question.id && submission.answer
      )
  ).length;

  function confirmSubmitTest() {
    Modal.confirm({
      centered: true,
      icon: null,
      title: (
        <span style={{ color: "#ffffff" }}>Submit Test?</span>
      ),
      content: (
        <span style={{ color: "#ffffff" }}>
          You have answered {answeredCount} of {questions.length} questions.
          Once submitted, you cannot change your answers. Are you sure you want
          to submit the test?
        </span>
      ),
      okText: "Yes, Submit",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      cancelButtonProps: {
        ghost: true,
        style: { borderColor: "#EC171E", color: "#EC171E" },
      },
      styles: {
        content: {
          background: "linear-gradient(180deg, #2d2d2d 0%, #232323 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 18,
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
        },
      },
      onOk: submitTest,
    });
  }

  return (
    <div className="my-active-session-container">
      <Row className="active-session-body">
        <Col xs={24} lg={16}>
          <div className="active-session-question-container">
            <div className="active-session-headers-pills">
              <div className="active-exam-session-pill">
                <span className="pill-label">Exam ID</span>
                <span className="pill-value">
                  {modifyId(saveInstance.exam_id)}
                </span>
              </div>
              <div className="active-exam-session-pill">
                <span className="pill-label">Exam Name</span>
                <span className="pill-value">{saveInstance.exam_name}</span>
              </div>
              <div className="active-exam-session-pill">
                <span className="pill-label">Score</span>
                <span className="pill-value">{currentQuestion.score}</span>
              </div>
            </div>

            <h5 className="active-session-question-title">
              Question {currentQuestionNumber + 1}
              <span className="active-session-question-count">
                {" "}
                / {questions.length}
              </span>
            </h5>
            <div className="active-session-question-card">
              <MarkdownPreview source={currentQuestion.description} />
            </div>
            {currentQuestion.file ? (
              <div className="active-session-file-download">
                <IconPrimary
                  onClick={() =>
                    downloadFile(
                      currentQuestion.file,
                      `question_${currentQuestionNumber}_file`
                    )
                  }
                >
                  <CopyIcon />
                </IconPrimary>
              </div>
            ) : null}

            {currentQuestion.selectedAnswer ? (
              <div className="active-session-answered-chip">
                Answered: {currentQuestion.selectedAnswer}
              </div>
            ) : null}

            {currentQuestion.type === "flag" ? (
              <div className="active-session-flag-row">
                <Input
                  placeholder="Flag"
                  value={flagValue}
                  onChange={(e) => setFlagValue(e.target.value)}
                  onPressEnter={saveExamInstance}
                />
                <Button type="primary" onClick={saveExamInstance}>
                  Submit
                </Button>
              </div>
            ) : (
              <div className="active-session-options">
                {currentQuestion.option?.length ? (
                  <div className="active-session-options-grid" role="radiogroup">
                    {currentQuestion.option.map(
                      (option: any, index: number) => (
                        <div
                          key={index}
                          onClick={() => handleChooseOption(index)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleChooseOption(index);
                            }
                          }}
                          role="radio"
                          tabIndex={0}
                          className="active-session-option-container"
                          aria-checked={
                            currentQuestion.selectedAnswer ===
                            currentQuestion.option[index]
                          }
                        >
                          <span className="option-badge">{index + 1}</span>
                          <span className="option-text">{option}</span>
                        </div>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Shared by both answer types — flag questions need to page through
                the paper just as much as MCQs do. */}
            <div className="active-session-question-nav">
              <Button
                onClick={goToPrevQuestion}
                disabled={currentQuestionNumber < 1}
              >
                Prev
              </Button>
              <Button
                type="primary"
                onClick={goToNextQuestion}
                disabled={currentQuestionNumber === questions.length - 1}
              >
                Next
              </Button>
            </div>
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="active-session-question-sider">
            <div className="active-session-legend">
              {questionStatusLegend.map(({ statusClass, label }) => (
                <div className="legend-item" key={statusClass}>
                  <span className={`legend-dot ${statusClass}`} />
                  {label}
                </div>
              ))}
            </div>

            <div className="active-session-questions-numbers-container">
              <h4>Questions</h4>
              <p className="active-session-progress">
                {answeredCount} of {questions.length} answered
              </p>
              <div className="active-session-question-number-container">
                {Array.from({ length: questions.length }).map((_, index) => (
                  <div
                    key={index}
                    className="active-session-question-number"
                    onClick={() => setCurrentQuestionNumber(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setCurrentQuestionNumber(index);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-current={index === currentQuestionNumber}
                    data-status={
                      questions[index].answerStatus || "question-not-visited"
                    }
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <div className="active-session-footer">
        <button
          type="button"
          className="mark-toggle"
          onClick={handleMarked}
          aria-pressed={Boolean(currentQuestion.marked)}
        >
          {currentQuestion.marked ? <FullStarIcon /> : <EmptyStarIcon />}
          <span>Mark</span>
        </button>
        <div className="active-session-footer-nav">
          <Button
            onClick={goToNextQuestion}
            disabled={currentQuestionNumber === questions.length - 1}
          >
            Next
          </Button>
          <Button
            type="primary"
            className="submit-test-btn"
            onClick={confirmSubmitTest}
          >
            Submit Test
          </Button>
        </div>
      </div>
    </div>
  );
}
